/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {formatDate} from "@angular/common";
import {Component, Inject, OnInit} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {FabMessageService} from '@fabric/components/message';
import {DISMISS_MSG} from '../../../common/constants';
import {getDefaultYearRange, range} from '../../../core/helpers/generic-helpers';
import {LoadingCounter} from '../../../core/helpers/loading-counter';
import {StateService} from '../../../core/state/state.service';
import {IndustryQueryRequestDTO} from '../../../models/industry-query-request-dto.model';
import {IndustryScenario} from '../../../models/industry-scenario-dto.model';
import {SelectableOptions} from '../../../models/selectable-options';
import {ExcelExportService} from '../../../services/excel-export.service';
import {IndustryScenarioService} from '../../../services/industry-scenario.service';
import {MarketAreaService} from '../../../services/market-area.service';
import {PlanningMarketService} from '../../../services/planning-market.service';
import {RegionService} from '../../../services/region.service';
import {SubregionService} from '../../../services/subregion.service';

@Component({
    selector: 'app-query-industry-pop-up',
    templateUrl: './query-industry-pop-up.component.html',
    styleUrl: './query-industry-pop-up.component.scss',
    standalone: false
})
export class QueryIndustryPopUpComponent implements OnInit {
  readonly loading = new LoadingCounter();
  regions: any[];
  selectedRegion: string;
  subregions: string[];
  selectedSubregion: string;
  marketFlag: boolean;
  planningMarkets: SelectableOptions = new SelectableOptions();
  segmentLevel = ['Total Vehicle', 'Global Segment', 'Global Segment 2', 'Propulsion Segment'];
  selectedSegmentLevel: string;
  singleTab: boolean;
  volumeOutput: boolean;
  mixOutput: boolean;
  validateOutput: boolean;
  startYear: number;
  endYear: number;
  user: string;
  scenario: IndustryScenario;
  scenarioYearRange: number[];


  constructor(@Inject(MAT_DIALOG_DATA) private readonly data: any,
              public dialogRef: MatDialogRef<QueryIndustryPopUpComponent>,
              public stateService: StateService,
              public industryScenarioService: IndustryScenarioService,
              public regionService: RegionService,
              public subregionService: SubregionService,
              public planningMarketService: PlanningMarketService,
              public marketAreaService: MarketAreaService,
              public excelExportService: ExcelExportService,
              public snackBar: FabMessageService) {
    if (data) {
      this.scenario = data.scenario;
    }
  }

  get showSpinner(): boolean {
    return this.loading.active;
  }

  ngOnInit(): void {
    this.user = this.stateService.getUserGmid();
    this.planningMarkets.selectedOptions = [];
    this.marketFlag = false;
    this.getRegions();
    this.getYearRange();
    this.singleTab = false;

  }

  /**
   * Fetches all regions by industry scenario from the regionService and assigns them to the regions property.
   * If there is only one region, it automatically selects it and fetches the corresponding subregions or market areas.
   */
  getRegions(): void {
    this.loading.track(this.regionService.getAllRegionsByIndustryScenario(this.user, this.scenario.id)).subscribe(
      data => {
        this.regions = data;
        this.regions.sort((a, b) => {
          const regionA = a.region.toUpperCase(); // ignore upper and lowercase
          const regionB = b.region.toUpperCase(); // ignore upper and lowercase
          if (regionA < regionB) {
            return -1;
          }
          if (regionA > regionB) {
            return 1;
          }

          // names must be equal
          return 0;
        });
      },
      (err) => {
        this.snackBar.displayError(err, DISMISS_MSG);
      },
      () => {
        if (this.regions.length === 1) {
          this.selectedRegion = this.regions[0].region;
          this.getSubregionsOrMarketAreas();
        }
      }
    );
  }

  /**
   *  Fetches all subregions by region and industry scenario from the subregionService and assigns them to the subregions property.
   */
  getSubregions(): void {
    this.loading.track(this.subregionService.getSubregionsByRegionAndIndScenId(this.selectedRegion, this.scenario.id,
      this.user)).subscribe(
      data => {
        this.subregions = ['All'];
        data.forEach((value: string) => {
          this.subregions.push(value);
        })

      },
      (err) => {
        this.snackBar.displayError(err, DISMISS_MSG);
      }
    );
  }

  /**
   * Fetches all market areas by region and industry scenario from the marketAreaService and assigns them to the subregions property.
   */
  getMarketAreas(): void {
    this.loading.track(this.marketAreaService.getMarketAreasByRegionAndIndustryScenario(this.selectedRegion,
      this.scenario.id, this.user)).subscribe(
      data => {
        this.subregions = ['All'];
        data.forEach((value: string) => {
          this.subregions.push(value);
        })
      },
      (err) => {
        this.snackBar.displayError(err, DISMISS_MSG);
      }
    );
  }

  /**
   * Fetches all planning markets by market area or subregion from the planningMarketService and assigns them to the planningMarkets property.
   */
  getPlanningMarkets(): void {
    this.loading.track(this.planningMarketService.getPlanningMarketsByMarketAreaOrSubregion(this.selectedSubregion,
      this.scenario.id, this.marketFlag)).subscribe(
      data => {
        this.planningMarkets.options = data;
        this.planningMarkets.selectedOptions = [];
      },
      (err) => {
        this.snackBar.displayError(err, DISMISS_MSG);
      }
    );
  }

  /**
   * Fetches the year range from the industryScenarioService and assigns the start and end years.
   */
  getYearRange(): void {
    this.loading.track(this.industryScenarioService.getYearRange(this.scenario.id)).subscribe(
      data => {
        const scenarioMinYear = data[0].MIN_YEAR;
        const scenarioMaxYear = data[0].MAX_YEAR;
        const {startYear, endYear} = getDefaultYearRange();
        // If there is no data returned from the scenario, use the default year range
        if (scenarioMinYear == null || scenarioMaxYear == null) {
          this.startYear = startYear;
          this.endYear = endYear;
          this.scenarioYearRange = range(this.startYear - 20, this.endYear + 21);
        } else if (startYear < scenarioMaxYear) {
          // Year range is +- 20 of the current year range of the scenario
          // If the default year range is a valid range of volumes in the scenario, then use the default year range.
          // else use the year range values from the scenario
          this.startYear = Math.max(scenarioMinYear, startYear);
          this.endYear = Math.min(endYear, scenarioMaxYear);
          this.scenarioYearRange = range(Math.min(scenarioMinYear, startYear) - 20, Math.max(endYear, scenarioMaxYear) + 21);
        } else {
          this.startYear = scenarioMinYear;
          this.endYear = scenarioMaxYear;
          this.scenarioYearRange = range(this.startYear - 20, this.endYear + 21);
        }
      },
      (err) => {
        this.snackBar.displayError(err, DISMISS_MSG);
      }
    );
  }

  /**
   * Checks if a region is selected and then fetches either the subregions or market areas based on the marketFlag property.
   */
  getSubregionsOrMarketAreas(): void {
    this.selectedSubregion = null;
    this.planningMarkets.selectedOptions = [];
    if (this.selectedRegion === null || this.selectedRegion === undefined) {
      return;
    }
    if (this.marketFlag) {
      this.getMarketAreas();
    } else {
      this.getSubregions();
    }
  }

  /**
   * Constructs a request object and sends it to the industryScenarioService to query the industry.
   * If successful, it exports the received data to an Excel file.
   */
  queryIndustry(): void {
    const currDate = formatDate(new Date(), 'yyyy-MM-dd', 'en');
    const fileName = 'IndFT_' + this.scenario.id + "_" + this.scenario.industryScenarioName + '_' + currDate;

    const request: IndustryQueryRequestDTO = {
      gmid: this.user,
      region: this.selectedRegion,
      mktFlag: this.marketFlag ? 1 : 0,
      indScenId: this.scenario.id,
      market: this.selectedSubregion,
      segment: 'All',
      planningMarkets: this.planningMarkets.selectedOptions.join('\'\', \'\''),
      segmentScheme: this.selectedSegmentLevel,
      startYear: this.startYear,
      endYear: this.endYear,
      functionFlag: this.getQueryFunction(),
      isSingleSheet: this.singleTab
    }
    this.loading.track(this.industryScenarioService.queryIndustry(request)).subscribe(
      (file: any) => {
        this.excelExportService.exportToExcelFromServer(file, fileName);
      },
      () => {
        this.snackBar.displayError('Error with exporting data.', DISMISS_MSG);
      }, () => {
        this.dialogRef.close();
      });
  }

  /**
   * Determines the function flag for the query based on the selected output options.
   * @returns The function flag.
   */
  getQueryFunction(): number {
    if (this.validateOutput) {
      return 4;
    } else if (this.volumeOutput && !this.mixOutput) {
      return 1;
    } else if (this.mixOutput && !this.volumeOutput) {
      return 2;
    } else if (this.mixOutput && this.volumeOutput) {
      return 3;
    } else {
      return 0;
    }
  }

  /**
   * Checks if the validate output option is selected and if so, resets the other output options and the selected segment level.
   */
  onValidateCheck(): void {
    if (this.validateOutput) {
      this.volumeOutput = false;
      this.mixOutput = false;
      this.singleTab = false;
      this.selectedSegmentLevel = null;
    }
  }

  /**
   * Checks if the selected segment level is 'Total Vehicle' and if so, resets the single tab option.
   */
  onTotalVehicleSelect(): void {
    if (this.selectedSegmentLevel === 'Total Vehicle') {
      this.singleTab = false;
    }
  }

  /**
   * activates the submit button if the state of the pop up allows
   */
  isButtonDisabled(): boolean {
    const locationFieldsEmpty = this.selectedRegion === null || this.selectedRegion === undefined ||
      this.selectedSubregion === null || this.selectedSubregion === undefined ||
      this.planningMarkets.selectedOptions.length === 0;
    const segmentLevelNotSelected = this.getQueryFunction() !== 4 &&
      (this.selectedSegmentLevel === null || this.selectedSegmentLevel === undefined);
    return locationFieldsEmpty || this.getQueryFunction() === 0 || segmentLevelNotSelected;
  }
}
